import { useEffect, useState } from "react";
import { api } from "../services/api";
import { painelDefault } from "../utils/painelDefault"; 
import { 
  Building2, 
  Upload, 
  Save, 
  Files, 
  Settings, 
  Link as LinkIcon, 
  Loader2,
  FileText,
  AlertCircle,
  X,
  Trash2,
  Plus
} from "lucide-react";
import "./Documentos.css";

// === CHAVE DA ÁREA DE CONTROLES INTERNOS ===
const AREA_KEY = "controlesInternos";
// ===========================================

export default function ControlesInternos() {
  const [painelCompleto, setPainelCompleto] = useState(null);
  const [categoriaData, setCategoriaData] = useState(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [needsInit, setNeedsInit] = useState(false);

  // Estados do Modal de Empilhamento
  const [modalItem, setModalItem] = useState(null);
  const [novoDocTitulo, setNovoDocTitulo] = useState("");
  const [novoDocAno, setNovoDocAno] = useState(new Date().getFullYear().toString());
  const [novoDocArquivo, setNovoDocArquivo] = useState(null);
  const [uploadingModal, setUploadingModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const siteId = localStorage.getItem("siteId");
      if (!siteId) return;

      const response = await api.get(`/painel?cliente=${siteId}`);
      const dados = response.data;
      
      setPainelCompleto(dados);
      
      if (dados && dados.dados_areas && dados.dados_areas[AREA_KEY]) {
        setCategoriaData(dados.dados_areas[AREA_KEY]);
        setNeedsInit(false);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setNeedsInit(true);
      } else {
        console.error("Erro ao carregar dados:", error);
      }
    }
  }

  async function handleInitialize() {
    setIsLoading(true);
    try {
      const siteId = localStorage.getItem("siteId");
      const payload = { cliente_id: siteId, dados_areas: painelDefault };

      await api.post(`/painel`, payload);
      alert("Estrutura do painel gerada com sucesso na base de dados.");
      loadData(); 
    } catch (error) {
      alert("Erro crítico ao tentar criar a estrutura inicial.");
    } finally {
      setIsLoading(false);
    }
  }

  const handleLinkChange = (id, novoLink) => {
    setCategoriaData((prevCategoria) => {
      if (!prevCategoria || !prevCategoria.itens) return prevCategoria;

      const novosItens = prevCategoria.itens.map((item) => {
        if (item.id === id) {
          return { 
            ...item, 
            link: novoLink, 
            disponivel: novoLink !== "#" && novoLink !== "" 
          };
        }
        return item;
      });

      return { ...prevCategoria, itens: novosItens };
    });
  };

  const handleFileUpload = async (e, docId) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsLoading(true);
    try {
      const siteId = localStorage.getItem("siteId");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("cliente_id", siteId);

      const response = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const fileUrl = response.data.url || response.data.link || response.data.location;
      if (!fileUrl) {
        throw new Error("A API não retornou a URL do arquivo.");
      }

      handleLinkChange(docId, fileUrl);
    } catch (error) {
      console.error("Erro no upload:", error);
      alert("Falha ao enviar o arquivo.");
    } finally {
      setIsLoading(false);
      e.target.value = ""; 
    }
  };

  // --- FUNÇÕES DO MODAL DE EMPILHAMENTO ---
  const handleOpenEmpilhamentoModal = (doc) => {
    setModalItem({
      ...doc,
      arquivos: Array.isArray(doc.arquivos) ? doc.arquivos : []
    });
    setNovoDocTitulo("");
    setNovoDocAno(new Date().getFullYear().toString());
    setNovoDocArquivo(null);
  };

  const handleAddArquivoEmpilhamento = async () => {
    if (!novoDocTitulo.trim()) {
      alert("Informe o título do documento.");
      return;
    }
    if (!novoDocArquivo) {
      alert("Selecione o arquivo PDF para upload.");
      return;
    }

    setUploadingModal(true);
    try {
      const siteId = localStorage.getItem("siteId");
      const formData = new FormData();
      formData.append("file", novoDocArquivo);
      formData.append("cliente_id", siteId);

      const response = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const fileUrl = response.data.url || response.data.link || response.data.location;
      if (!fileUrl) {
        throw new Error("A API não retornou a URL do arquivo.");
      }

      const novoArquivoObj = {
        id: Date.now(),
        titulo: novoDocTitulo.trim(),
        exercicio: novoDocAno.trim(),
        link: fileUrl,
        data_publicacao: new Date().toISOString()
      };

      // 1. Atualiza a lista do modal
      setModalItem((prev) => ({
        ...prev,
        arquivos: [novoArquivoObj, ...(prev?.arquivos || [])],
        disponivel: true
      }));

      // 2. Atualiza os itens da categoria de forma funcional
      setCategoriaData((prevCategoria) => {
        if (!prevCategoria || !prevCategoria.itens) return prevCategoria;

        const novosItens = prevCategoria.itens.map((it) => {
          if (it.id === modalItem.id) {
            const listaExistente = Array.isArray(it.arquivos) ? it.arquivos : [];
            return {
              ...it,
              arquivos: [novoArquivoObj, ...listaExistente],
              disponivel: true
            };
          }
          return it;
        });

        return { ...prevCategoria, itens: novosItens };
      });

      // 3. Limpa os campos
      setNovoDocTitulo("");
      setNovoDocArquivo(null);
      const fileInput = document.getElementById("file-upload-controles-modal");
      if (fileInput) fileInput.value = "";

    } catch (err) {
      console.error("Erro no upload do documento de controles internos:", err);
      alert("Erro ao enviar o documento. Verifique o console ou a aba Network.");
    } finally {
      setUploadingModal(false);
    }
  };

  const handleDeleteArquivoEmpilhamento = (arquivoId) => {
    if (!window.confirm("Deseja realmente remover este arquivo da lista?")) return;

    setModalItem((prev) => {
      const listaAtualizada = (prev?.arquivos || []).filter((arq) => arq.id !== arquivoId);
      return {
        ...prev,
        arquivos: listaAtualizada,
        disponivel: listaAtualizada.length > 0
      };
    });

    setCategoriaData((prevCategoria) => {
      if (!prevCategoria || !prevCategoria.itens) return prevCategoria;

      const novosItens = prevCategoria.itens.map((it) => {
        if (it.id === modalItem.id) {
          const listaAtualizada = (it.arquivos || []).filter((arq) => arq.id !== arquivoId);
          return {
            ...it,
            arquivos: listaAtualizada,
            disponivel: listaAtualizada.length > 0
          };
        }
        return it;
      });

      return { ...prevCategoria, itens: novosItens };
    });
  };

  async function handleSave() {
    setIsLoading(true);
    try {
      const siteId = localStorage.getItem("siteId");
      
      const payload = {
        dados_areas: {
          ...painelCompleto.dados_areas,
          [AREA_KEY]: categoriaData
        }
      };

      await api.put(`/painel/${siteId}`, payload);
      alert("Configurações de Controles Internos atualizadas com sucesso.");
      loadData(); 
    } catch (error) {
      console.error("Erro ao salvar controles internos:", error);
      alert("Erro ao tentar salvar as alterações.");
    } finally {
      setIsLoading(false);
    }
  }

  if (needsInit) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-wrapper">
          <div className="init-container">
            <div className="init-icon-box">
              <Settings size={48} strokeWidth={1.5} />
            </div>
            <h2>Estrutura Não Configurada</h2>
            <p>A estrutura base de gerenciamento de Controles Internos ainda não foi gerada no banco de dados.</p>
            <button className="btn btn-primary" onClick={handleInitialize} disabled={isLoading}>
              {isLoading ? (
                <><Loader2 size={18} className="spinner" /> Inicializando...</>
              ) : (
                <><Settings size={18} /> Inicializar Controles Internos</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!categoriaData) {
    return (
      <div className="dashboard-loading">
        <Loader2 className="spinner" size={40} />
        <p>Carregando painel de controles internos...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">
        
        <div className="dashboard-header">
          <div className="dashboard-title">
            <h1>{categoriaData.titulo || "Controles Internos"}</h1>
            <p>{categoriaData.descricao || "Gerencie relatórios de controle interno, certificações e manuais de risco."}</p>
          </div>
        </div>

        <div className="config-card p-0">
          <div className="card-header">
            <Building2 size={20} className="card-header-icon" />
            <h3>Documentos e Publicações</h3>
          </div>

          <div className="document-list">
            {categoriaData.itens.map((doc) => (
              <div className="document-item" key={doc.id}>
                
                <div className="document-info">
                  <div className="document-icon">
                    <FileText size={20} />
                  </div>
                  <div className="document-text">
                    <h4>{doc.nome}</h4>
                    <span className="document-note">
                      <AlertCircle size={14} className="inline-icon"/> {doc.nota}
                    </span>
                  </div>
                </div>

                <div className="document-action-area">
                  {doc.nota && doc.nota.includes("Empilhamento") ? (
                    <div className="empilhamento-box">
                      <Files size={24} className="empilhamento-icon" />
                      <div className="empilhamento-info">
                        <p><strong>{doc.arquivos?.length || 0} arquivo(s)</strong> no histórico</p>
                        <span className="file-upload-hint">Gerencie relatórios acumulativos</span>
                      </div>
                      
                      <button 
                        type="button" 
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleOpenEmpilhamentoModal(doc)}
                      >
                        <Upload size={14} /> Gerenciar / Adicionar
                      </button>
                    </div>
                  ) : (
                    <div className="upload-row">
                      <div className="input-with-icon">
                        <LinkIcon size={16} className="input-icon" />
                        <input 
                          type="text" 
                          className="form-input" 
                          value={doc.link === "#" ? "" : doc.link || ""} 
                          placeholder="Cole a URL do documento aqui..."
                          onChange={(e) => handleLinkChange(doc.id, e.target.value)}
                        />
                      </div>
                      <span className="or-divider">ou</span>
                      <label className="btn btn-secondary btn-sm upload-btn">
                        {isLoading ? <Loader2 size={16} className="spinner" /> : <Upload size={16} />}
                        <span>Upload PDF</span>
                        <input 
                          type="file" 
                          hidden 
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => handleFileUpload(e, doc.id)}
                          disabled={isLoading}
                        />
                      </label>
                    </div>
                  )}
                </div>

              </div>
            ))}
          </div>

          <div className="config-footer">
            <button className="btn btn-primary" onClick={handleSave} disabled={isLoading}>
              {isLoading ? (
                <><Loader2 size={18} className="spinner" /> Salvando...</>
              ) : (
                <><Save size={18} /> Salvar Alterações de Controles Internos</>
              )}
            </button>
          </div>
        </div>

      </div>

      {/* --- MODAL PARA GERENCIAR EMPILHAMENTO DE CONTROLES INTERNOS --- */}
      {modalItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h3>{modalItem.nome}</h3>
                <p className="modal-subtitle">Adicione relatórios ou certificações acumulativas deste item.</p>
              </div>
              <button 
                type="button" 
                className="btn-close-modal" 
                onClick={() => setModalItem(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-add-form">
              <h4>Novo Documento</h4>
              <div className="modal-form-grid">
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="Título (ex: Relatório Anual de Controle Interno)"
                  value={novoDocTitulo}
                  onChange={(e) => setNovoDocTitulo(e.target.value)}
                />
                <input 
                  type="number" 
                  className="form-input" 
                  style={{ width: "110px" }}
                  placeholder="Ano"
                  value={novoDocAno}
                  onChange={(e) => setNovoDocAno(e.target.value)}
                />
                <input 
                  id="file-upload-controles-modal"
                  type="file" 
                  className="form-input file-input-modal" 
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setNovoDocArquivo(e.target.files[0] || null)}
                />
                <button 
                  type="button" 
                  className="btn btn-primary btn-sm"
                  onClick={handleAddArquivoEmpilhamento}
                  disabled={uploadingModal}
                >
                  {uploadingModal ? (
                    <><Loader2 size={14} className="spinner" /> Enviando...</>
                  ) : (
                    <><Plus size={14} /> Inserir</>
                  )}
                </button>
              </div>
            </div>

            <div className="modal-files-list">
              <h4>Documentos Cadastrados ({modalItem.arquivos?.length || 0})</h4>
              {modalItem.arquivos && modalItem.arquivos.length > 0 ? (
                <div className="modal-table-wrapper">
                  <table className="modal-table">
                    <thead>
                      <tr>
                        <th>Ano</th>
                        <th>Título</th>
                        <th style={{ textAlign: "right" }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalItem.arquivos.map((arq) => (
                        <tr key={arq.id}>
                          <td><strong>{arq.exercicio || arq.ano}</strong></td>
                          <td>
                            <a href={arq.link} target="_blank" rel="noreferrer" className="modal-link">
                              {arq.titulo}
                            </a>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <button 
                              type="button" 
                              className="btn-delete-row"
                              title="Remover documento"
                              onClick={() => handleDeleteArquivoEmpilhamento(arq.id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="empty-modal-text">Nenhum relatório anexado a este item ainda.</p>
              )}
            </div>

            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={() => setModalItem(null)}
              >
                Concluir Edição
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}